import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { canTranscribeMedia } from "@app/lib/player/mediaSources.js";
import * as m from "@app/paraglide/messages.js";

const STORAGE_KEY = "cc-transcribe-after-import";
export const TRANSCRIBE_AFTER_IMPORT_ALWAYS = "always";
export const TRANSCRIBE_AFTER_IMPORT_NEVER = "never";

export function getTranscribeAfterImportPref() {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        if (
            value === TRANSCRIBE_AFTER_IMPORT_ALWAYS ||
            value === TRANSCRIBE_AFTER_IMPORT_NEVER
        ) {
            return value;
        }
    } catch (err) {
        console.error(err);
    }
    return null;
}

export function setTranscribeAfterImportPref(value) {
    if (
        value !== TRANSCRIBE_AFTER_IMPORT_ALWAYS &&
        value !== TRANSCRIBE_AFTER_IMPORT_NEVER
    ) {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY, value);
    } catch (err) {
        console.error(err);
    }
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function readDontAskAgain() {
    const popup = Swal.getPopup();
    return !!popup?.querySelector("#transcribe-after-import-dont-ask")?.checked;
}

/**
 * After media import, ask whether to open Automatic Transcription.
 * No-ops when offline or the current media source cannot be transcribed.
 */
export async function promptAutomaticTranscription() {
    if (!environment.online || !canTranscribeMedia(projectState.media)) {
        return;
    }

    const pref = getTranscribeAfterImportPref();
    if (pref === TRANSCRIBE_AFTER_IMPORT_NEVER) {
        return;
    }
    if (pref === TRANSCRIBE_AFTER_IMPORT_ALWAYS) {
        showModal("automaticTranscription");
        return;
    }

    let dontAskAgain = false;
    const result = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.transcribe_after_import_title(),
        html: `<p class="mb-3">${escapeHtml(m.transcribe_after_import_text())}</p>
            <div class="form-check text-start">
                <input class="form-check-input" type="checkbox" id="transcribe-after-import-dont-ask">
                <label class="form-check-label" for="transcribe-after-import-dont-ask">${escapeHtml(m.transcribe_after_import_dont_ask_again())}</label>
            </div>`,
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.yes(),
        denyButtonText: m.no(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
        preConfirm: () => {
            dontAskAgain = readDontAskAgain();
        },
        preDeny: () => {
            dontAskAgain = readDontAskAgain();
        },
    });

    if (dontAskAgain) {
        setTranscribeAfterImportPref(
            result.isConfirmed
                ? TRANSCRIBE_AFTER_IMPORT_ALWAYS
                : TRANSCRIBE_AFTER_IMPORT_NEVER,
        );
    }

    if (result.isConfirmed) {
        showModal("automaticTranscription");
    }
}
